import React, { MutableRefObject } from 'react';
import { styled } from '@mui/material';
import { EditOff } from '@mui/icons-material';
import moment from 'moment';
import { Colors } from 'library';
import { Row, Stack, Text } from 'library/ui';
import { ChatLockReason, Message } from 'models/Message';
import { Thread } from './Thread';
import { ThreadHeader } from './ThreadHeader/ThreadHeader';
import ChatGuidelines from './ChatGuidelines';
import { isSameDay, isSameYear, subDays } from 'date-fns';
import { formatShortWeekdayMonthDay } from 'shared/Dates';

function dayLabel(date: string) {
    const dateObject = new Date(date);
    const now = new Date();
    if (isSameDay(dateObject, now)) return 'Today';
    if (isSameDay(dateObject, subDays(now, 1))) return 'Yesterday';
    return `${formatShortWeekdayMonthDay(date)}${isSameYear(dateObject, now) ? '' : `, ${dateObject.getFullYear()}`}`;
}

function lockedCopy(reason?: ChatLockReason | null) {
    if (reason === 'BLOCKED') return 'You have blocked this worker, so this chat is closed.';
    if (reason === 'NO_SHIFT') return 'This chat opens an hour before a shift with this worker.';
    return 'This chat is from a previous shift and is read only.';
}

interface Props {
    providerIdObject: { user: { id: number } };
    providerName?: string;
    messages?: Message[];
    locked: boolean;
    lockReason?: ChatLockReason | null;
    user: { id: number };
    bottomOfThread: MutableRefObject<HTMLDivElement | null>;
    messageInput: JSX.Element;
    isMobile: boolean;
    providerImage?: string;
}

export default function ConversationView({
    providerIdObject,
    providerName,
    messages,
    locked,
    lockReason,
    user,
    bottomOfThread,
    messageInput,
    isMobile,
    providerImage,
}: Props) {
    return (
        <Stack grow gap="none">
            <ThreadHeader
                providerIdObject={providerIdObject}
                providerName={providerName ?? ''}
                isMobile={isMobile}
                providerImage={providerImage}
            />
            <Stack padding="sm">
                <ChatGuidelines />
            </Stack>
            <MessagesArea grow gap="sm" padding="sm">
                {messages?.map((message, i) => {
                    const showDivider =
                        i === 0 || !moment(message.sent_at).isSame(moment(messages[i - 1].sent_at), 'day');
                    return (
                        <React.Fragment key={message.id ?? i}>
                            {showDivider && (
                                <DateDivider>
                                    <DateLine />
                                    <Text size="sm" tone="secondary">
                                        {dayLabel(message.sent_at)}
                                    </Text>
                                    <DateLine />
                                </DateDivider>
                            )}
                            <Thread message={message} user={user} />
                        </React.Fragment>
                    );
                })}
                <div ref={bottomOfThread} />
            </MessagesArea>
            <InputArea padding="sm">
                {locked ? (
                    <Row gap="sm" align="center">
                        <EditOff sx={{ fontSize: 18, color: Colors.textSecondary }} />
                        <Text weight="semibold">{lockedCopy(lockReason)}</Text>
                    </Row>
                ) : (
                    messageInput
                )}
            </InputArea>
        </Stack>
    );
}

const MessagesArea = styled(Stack)({
    overflowY: 'auto',
    paddingTop: 0,
});

const InputArea = styled(Stack)({
    borderTop: `1px solid ${Colors.border}`,
});

const DateDivider = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    margin: '8px 0',
});

const DateLine = styled('div')({
    flex: 1,
    height: 1,
    backgroundColor: Colors.fill,
});
